import { ENTRY_CONFIG_TYPES, type EntryConfig } from "@/types/entryConfig";
import { buildAnnotationClassNames } from "@shared/annotationClassColors";
import {
  getAnnotationCategorySchemasForModel,
  type AnnotationCategorySchema,
  type AnnotationModelEntryType,
} from "@shared/annotationCategorySchemas";

function getModelOptions(entryConfig: EntryConfig | undefined) {
  if (entryConfig?.type === ENTRY_CONFIG_TYPES.STAND_COUNT_MEASUREMENT) {
    return {
      entryType: entryConfig.type as AnnotationModelEntryType,
      modelOptions: entryConfig.standCountMeasurementConfig?.imgMeasureConfig?.imageMeasurementModelOptions,
    };
  }
  if (entryConfig?.type === ENTRY_CONFIG_TYPES.IMAGE_MEASUREMENT) {
    return {
      entryType: entryConfig.type as AnnotationModelEntryType,
      modelOptions: entryConfig.imageMeasurementConfig?.imageMeasurementModelOptions,
    };
  }
  return undefined;
}

export function getAnnotationCategorySchemasForEntryConfig(
  entryConfig: EntryConfig | undefined
): AnnotationCategorySchema[] {
  const resolved = getModelOptions(entryConfig);
  if (!resolved?.modelOptions) return [];

  return getAnnotationCategorySchemasForModel(
    resolved.modelOptions.kind,
    resolved.entryType,
    resolved.modelOptions.kind === "openSetObjectDetector" ? resolved.modelOptions.prompts : []
  );
}

export function getAnnotationClassNamesForEntryConfig(entryConfig: EntryConfig | undefined): string[] {
  return getAnnotationCategorySchemasForEntryConfig(entryConfig).map(({ name }) => name);
}

export function getTaskAnnotationCategorySchemas(
  entryConfigs: readonly EntryConfig[]
): AnnotationCategorySchema[] {
  const schemasByName = new Map<string, AnnotationCategorySchema>();

  const keypointSignature = (schema: AnnotationCategorySchema): string | undefined => {
    if (!schema.keypointNames && !schema.keypointSkeleton) return undefined;
    return JSON.stringify({
      keypointNames: schema.keypointNames ?? null,
      keypointSkeleton: schema.keypointSkeleton ?? null,
    });
  };

  for (const schema of entryConfigs.flatMap(getAnnotationCategorySchemasForEntryConfig)) {
    const name = schema.name.trim();
    if (!name) continue;
    const existing = schemasByName.get(name);
    if (!existing) {
      schemasByName.set(name, {
        ...schema,
        name,
        ...(schema.keypointNames ? { keypointNames: [...schema.keypointNames] } : {}),
        ...(schema.keypointSkeleton
          ? { keypointSkeleton: schema.keypointSkeleton.map((pair) => [...pair]) }
          : {}),
      });
      continue;
    }
    const existingSignature = keypointSignature(existing);
    const incomingSignature = keypointSignature(schema);
    if (existingSignature && incomingSignature && existingSignature !== incomingSignature) {
      const signatures = [existingSignature, incomingSignature].sort();
      throw new Error(
        `Conflicting annotation category "${name}" keypoint schemas: ${signatures.join(" versus ")}`
      );
    }
    if (!existing.supercategory && schema.supercategory) existing.supercategory = schema.supercategory;
    if (!existing.keypointNames && schema.keypointNames) existing.keypointNames = [...schema.keypointNames];
    if (!existing.keypointSkeleton && schema.keypointSkeleton) {
      existing.keypointSkeleton = schema.keypointSkeleton.map((pair) => [...pair]);
    }
  }

  return [...schemasByName.values()];
}

export function getTaskAnnotationClassColorOrder(entryConfigs: readonly EntryConfig[]): string[] {
  return buildAnnotationClassNames(entryConfigs.flatMap(getAnnotationClassNamesForEntryConfig));
}

export function buildAnnotationClassNamesByEntryConfigId(
  entryConfigs: readonly EntryConfig[]
): ReadonlyMap<string, string[]> {
  return new Map(
    entryConfigs.flatMap((entryConfig) =>
      entryConfig.id ? [[entryConfig.id, getAnnotationClassNamesForEntryConfig(entryConfig)] as const] : []
    )
  );
}
